import React from 'react';
import PropTypes from 'prop-types';
import { Scrollbars } from 'react-custom-scrollbars';
import Waypoint from 'react-waypoint';
import debounce from 'lodash/debounce';
import Raven from 'raven-js';

import fullname from 'utils/fullname';
import Employees from 'api/resources/employees';
import getErrors from 'utils/getErrors';
import { Deserializer } from 'jsonapi-serializer';

import FilterItem from 'components/FilterItem';

import Popup from 'components/Popup';
import Loader from 'components/Loader';

import ErrorText from 'styled/Page/ErrorText';
import Input from 'styled/Input';
import DropdownList from 'styled/DropdownList';

const PAGE_SIZE = 20;
const VISIBLE_EMPLOYEES_NUMBER = 8;
const DEFAULT_VALUE = {
  label: 'Все',
  value: 'all',
};

class EmployeesAutocomplete extends React.PureComponent {
  input = React.createRef();

  state = {
    query: this.props.employee.label,
    isOpen: false,
    errors: [],
    hasMore: false,
    pageNumber: 0,
    employees: [],
    inputWasChanged: false,
  }

  loadEmployeesWithDebounce = debounce(() => {
    this.reloadEmployees();
  }, 500);

  componentDidUpdate(prevProps, prevState) {
    if (this.state.isOpen !== prevState.isOpen && this.state.isOpen) {
      this.reloadEmployees();
    }

    if (prevState.query !== this.state.query) {
      this.loadEmployeesWithDebounce();
    }
  }

  get popupPosition() {
    const DEFAULT_POSITION = 0;
    if (!this.input.current) return DEFAULT_POSITION;

    const CLIENT_HEIGHT = document.documentElement.clientHeight;
    const DROPDOWN_HEIGHT = 250;
    const OFFSET_TOP = this.input.current.offsetTop + this.input.current.offsetHeight;
    const OFFSET_BOTTOM = CLIENT_HEIGHT - (OFFSET_TOP - window.scrollY);

    if ((OFFSET_BOTTOM - DROPDOWN_HEIGHT) < 0) {
        return (`-${(
          DROPDOWN_HEIGHT / 2
          + this.input.current.offsetHeight
        )}px`
      );
    }
    return DEFAULT_POSITION;
  }

  handleInputFocus = () => {
    this.setState({ isOpen: true });
  }

  handleInputChange = (e) => {
    this.setState({
      query: e.target.value,
      inputWasChanged: true,
    });
  }

  handleSubmit = (e) => {
    e.preventDefault();
    this.reloadEmployees();
  }

  handleEmployeeClick = (activeEmployee) => {
    this.setState({
      isOpen: false,
      query: activeEmployee.label,
    });

    this.props.onChange(activeEmployee);

    if (this.state.employees.length <= VISIBLE_EMPLOYEES_NUMBER && this.state.hasMore) {
      this.loadEmployees();
    }
  }

  handleOnBackdropClick = () => {
    this.setState({
      isOpen: false,
      query: this.props.employee.label,
      inputWasChanged: false,
    });
  }

  reloadEmployees = () => {
    this.setState({
      pageNumber: 0,
      employees: [],
    }, this.loadEmployees);
  }

  loadEmployees = async () => {
    this.setState({ loading: true, errors: [] });

    try {
      const response = await Employees.getAll({
        page: {
          number: this.state.pageNumber,
          size: PAGE_SIZE,
        },
        filter: {
          is_disabled: false,
          q: this.state.inputWasChanged ? this.state.query : undefined,
        },
        fields: {
          employee: 'first_name,last_name',
        },
      });

      const employees = await new Deserializer({ keyForAttribute: 'camelCase' }).deserialize(response);

      this.setState(prevState => ({
        employees: prevState.employees.concat(employees),
        loading: false,
        hasMore: employees.length === PAGE_SIZE,
        pageNumber: prevState.pageNumber + 1,
      }));
    } catch (err) {
      this.setState({
        errors: await getErrors(err),
        loading: false,
      });

      Raven.captureException(err, { extra: this.props });
    }
  }

  render() {
    return (
      <React.Fragment>
        <form onSubmit={this.handleSubmit}>
          <Input
            value={this.state.query}
            onFocus={this.handleInputFocus}
            disabled={this.props.disabled}
            onChange={this.handleInputChange}
            ref={this.input}
          />
        </form>

        <div style={this.state.isOpen ? { position: 'relative', top: this.popupPosition } : {}}>
          <Popup
            isVisible={this.state.isOpen}
            onClickOnBackdrop={this.handleOnBackdropClick}
            isControlled
          >
            <DropdownList style={{ width: '319px' }}>
              <Scrollbars
                autoHide
                autoHeight
                autoHeightMax="210px"
              >
                <FilterItem
                  onClick={this.handleEmployeeClick}
                  item={DEFAULT_VALUE}
                  activeFilter={this.props.employee}
                  focused
                  tabIndex="0"
                />

                {this.state.employees.map((employee, index) => (
                  <FilterItem
                    key={employee.id}
                    onClick={this.handleEmployeeClick}
                    item={{
                      label: fullname(employee) || employee.id,
                      value: employee.id,
                    }}
                    activeFilter={this.props.employee}
                    tabIndex={index}
                  />
                ))}

                {!this.state.loading && this.state.errors.length === 0 && this.state.hasMore && (
                  <Waypoint onEnter={this.loadEmployees} />
                )}

                {this.state.errors === 0 && this.state.employees.length === 0 && !this.state.loading && (
                  <ErrorText role="alert" style={{ textAlign: 'center' }}>
                    Нет доступных для выбора сотрудников
                  </ErrorText>
                )}

                {this.state.loading && <Loader />}

                {this.state.errors.map(error => (
                  <ErrorText role="alert" key={error}>{error}</ErrorText>
                ))}
              </Scrollbars>
            </DropdownList>
          </Popup>
        </div>
      </React.Fragment>
    );
  }
}

EmployeesAutocomplete.propTypes = {
  employee: PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string,
  }).isRequired,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
};

EmployeesAutocomplete.defaultProps = {
  onChange: () => {},
  disabled: false,
};

export default EmployeesAutocomplete;
